import { v4 as uuid } from 'uuid';
import type { AccountData, LosIntegration, LosType, WizardState, WizardUser } from './types';
import { TOTAL_STEPS } from './types';
import { buildUserSchema } from './schemas/user.schema';

const USERS_STEP = 4;

export type WizardAction =
  | { type: 'goToStep'; step: number }
  | { type: 'editFromReview'; step: number }
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'setAccount'; data: Partial<AccountData> }
  | { type: 'addLos'; losType: LosType }
  | { type: 'updateLos'; index: number; credentials: Record<string, string> }
  | { type: 'removeLos'; index: number }
  | { type: 'toggleProduct'; urn: string }
  | { type: 'addUser'; id?: string }
  | { type: 'updateUser'; id: string; data: Partial<Omit<WizardUser, 'id'>> }
  | { type: 'removeUser'; id: string }
  | { type: 'setUserAccess'; id: string; access: Record<string, boolean> | null }
  | { type: 'setLosIntegrations'; integrations: LosIntegration[] }
  | { type: 'reset' };

export const initialWizardState: WizardState = {
  step: 1,
  returnToReview: false,
  account: {
    lenderName: '',
    lenderId: '',
    salesforceId: '',
    nmlsId: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    entityId: '',
    entityName: '',
    apiUsername: '',
    apiPassword: '',
  },
  losIntegrations: [],
  products: {},
  users: [],
};

const clamp = (step: number) => Math.min(Math.max(step, 1), TOTAL_STEPS);

// Same rule as Step 4's Next gate: a user must satisfy the schema of the LOS integrations selected
// now, so an integration added while editing Step 2 can leave an existing user incomplete.
const hasIncompleteUsers = (state: WizardState) => {
  const schema = buildUserSchema({
    requireMlConsumerUserName: state.losIntegrations.some((los) => los.type === 'ml-consumer'),
    requireMlMortgageUserName: state.losIntegrations.some((los) => los.type === 'ml-mortgage'),
  });
  return state.users.some((user) => !schema.safeParse(user).success);
};

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'goToStep':
      return { ...state, step: clamp(action.step) };
    case 'editFromReview':
      return { ...state, step: clamp(action.step), returnToReview: true };
    case 'next': {
      if (!state.returnToReview) return { ...state, step: clamp(state.step + 1) };
      // Straight back to the summary, unless skipping Step 4 would hide a user the edit left
      // incomplete: Step 4 flags which one, and its own Next then reaches the summary.
      const step =
        state.step < USERS_STEP && hasIncompleteUsers(state) ? USERS_STEP : TOTAL_STEPS;
      return { ...state, step, returnToReview: step !== TOTAL_STEPS };
    }
    case 'back':
      return { ...state, step: clamp(state.step - 1) };
    case 'setAccount':
      return { ...state, account: { ...state.account, ...action.data } };
    case 'addLos':
      return {
        ...state,
        losIntegrations: [...state.losIntegrations, { type: action.losType, credentials: {} }],
      };
    case 'updateLos':
      return {
        ...state,
        losIntegrations: state.losIntegrations.map((los, i) =>
          i === action.index ? { ...los, credentials: { ...los.credentials, ...action.credentials } } : los,
        ),
      };
    case 'removeLos':
      return {
        ...state,
        losIntegrations: state.losIntegrations.filter((_, i) => i !== action.index),
      };
    case 'toggleProduct': {
      const enabled = !state.products[action.urn];
      return {
        ...state,
        products: { ...state.products, [action.urn]: enabled },
        // A restricted user's access record is a snapshot of Step 3 taken when access was turned
        // on; a product enabled afterwards has no key there and would silently never reach the
        // user. Grant it on entry — a key the user explicitly unchecked stays false.
        users: enabled
          ? state.users.map((u) =>
              u.productAccess === null || action.urn in u.productAccess
                ? u
                : { ...u, productAccess: { ...u.productAccess, [action.urn]: true } },
            )
          : state.users,
      };
    }
    case 'addUser':
      return {
        ...state,
        users: [
          ...state.users,
          {
            // The step may supply the id so it can open the editor on the row it just created.
            id: action.id ?? uuid(),
            firstName: '',
            lastName: '',
            mlConsumerUserName: '',
            mlMortgageUserName: '',
            email: '',
            role: '',
            productAccess: null,
          },
        ],
      };
    case 'updateUser':
      return {
        ...state,
        users: state.users.map((u) => (u.id === action.id ? { ...u, ...action.data } : u)),
      };
    case 'removeUser':
      return { ...state, users: state.users.filter((u) => u.id !== action.id) };
    case 'setUserAccess':
      return {
        ...state,
        users: state.users.map((u) =>
          u.id === action.id ? { ...u, productAccess: action.access } : u,
        ),
      };
    case 'setLosIntegrations':
      return { ...state, losIntegrations: action.integrations };
    case 'reset':
      return initialWizardState;
    default:
      return state;
  }
}
